import { partials } from '@shopify/partial-rendering';
import { transition } from 'view-transition';

/**
 * <s-scroll-paginate> — Infinite-scroll pagination that appends subsequent pages
 * in-place as the user scrolls, with IntersectionObserver-based page tracking and
 * back-button restore.
 *
 * Attributes (on <s-scroll-paginate>):
 *   page                — Current page number (integer, default 1).
 *   total               — Total number of pages (integer, default 1).
 *   partial             — Name of the {% partial 'name' %} region to fetch; must exactly
 *                         match the partial wrapper in templates/collection.liquid or
 *                         templates/search.liquid — renaming either side silently breaks appends.
 *   data-loading-message — i18n string announced via #sr-announcer while fetching.
 *   data-loaded-message  — i18n string announced via #sr-announcer after a page appends.
 *
 * Required child markup:
 *   :scope > :first-child         — Grid container (firstElementChild); must have an `id`
 *                                   matching the `#<id>` queried in the fetched partial HTML.
 *   nav[aria-label="Pagination"]  — Standard pagination nav; hidden on mount, updated in-place
 *                                   when subsequent pages load.
 *
 * Generated markup:
 *   A sentinel <div aria-hidden="true"> appended after the grid; removed once the last page loads.
 *   [data-page] set on the first card of each loaded page for IntersectionObserver page tracking.
 *
 * External coupling:
 *   #sr-announcer (global) — Live region for loading/loaded announcements.
 *   history.replaceState   — URL ?page= param kept in sync with the visible page (canonical: omits page=1).
 */
// The `partial` HTML attribute names a {% partial 'name' %} region in the server-rendered page.
// The string passed here (e.g. "product-grid" or "search-results") must exactly match the
// {% partial %} wrapper in templates/collection.liquid or templates/search.liquid — renaming
// either side silently breaks infinite-scroll page appends.
class ScrollPaginate extends HTMLElement {
  #page;
  #total;
  #partial;
  #grid;
  #nav;
  #sentinel;
  #loading = false;
  #loadObserver;
  #pageObserver;
  #abortController;
  #restoreController;
  #rafId;

  connectedCallback() {
    this.#page = parseInt(this.getAttribute('page'), 10) || 1;
    this.#total = parseInt(this.getAttribute('total'), 10) || 1;
    this.#partial = this.getAttribute('partial');
    this.#grid = this.firstElementChild;
    this.#nav = this.querySelector('nav[aria-label="Pagination"]');
    if (!this.#grid || this.#total <= 1 && this.#page === 1) return;

    if (this.#nav) this.#nav.hidden = true;

    // Mark the first card of the initial page for scroll tracking
    const firstCard = this.#grid.firstElementChild;
    if (firstCard) firstCard.dataset.page = this.#page;

    // Sentinel for triggering next-page loads
    this.#sentinel = document.createElement('div');
    this.#sentinel.setAttribute('aria-hidden', 'true');
    this.#grid.after(this.#sentinel);

    // Load-more observer
    this.#loadObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !this.#loading) this.#loadNext();
    }, { rootMargin: '200px' });
    if (this.#page < this.#total) this.#loadObserver.observe(this.#sentinel);

    // Page-tracking observer — fires when a marker crosses the viewport midpoint
    this.#pageObserver = new IntersectionObserver(async (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          const p = parseInt(entry.target.dataset.page, 10);
          // Let the browser paint before URL update
          await new Promise(resolve => setTimeout(resolve, 0));
          if (!this.isConnected) return;
          const url = new URL(location.href);
          // Shopify convention: omit ?page=1 entirely — deleting rather than setting page=1
          // keeps URLs canonical and matches what Shopify's own pagination links produce.
          if (p <= 1) { url.searchParams.delete('page'); }
          else { url.searchParams.set('page', String(p)); }
          history.replaceState(null, '', url);
        }
      }
    }, { rootMargin: '-50% 0px -50% 0px' });
    if (firstCard) this.#pageObserver.observe(firstCard);

    // Back-button restore: if page > 1, fetch earlier pages and prepend
    if (this.#page > 1) this.#restorePages();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#loadObserver?.disconnect();
    this.#pageObserver?.disconnect();
    this.#restoreController?.abort();
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
  }

  async #loadNext() {
    // Abort in-flight page fetch so fast scrolls don't let a stale page land after a newer one
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#loading = true;
    const nextPage = this.#page + 1;

    const loadingAnnouncer = document.getElementById('sr-announcer');
    if (loadingAnnouncer) {
      loadingAnnouncer.textContent = '';
      cancelAnimationFrame(this.#rafId);
      this.#rafId = requestAnimationFrame(() => {
        loadingAnnouncer.textContent = this.dataset.loadingMessage || '';
        this.#rafId = null;
      });
    }

    try {
      const { cards, nav } = await this.#fetchPage(nextPage, this.#abortController.signal);
      if (!cards || cards.length === 0) {
        cancelAnimationFrame(this.#rafId);
        const a = document.getElementById('sr-announcer');
        if (a) a.textContent = '';
        return;
      }

      // Mark the first card of this batch for page tracking
      cards[0].dataset.page = nextPage;
      this.#pageObserver.observe(cards[0]);

      await transition(() => {
        this.#grid.append(...cards);
        if (nav && this.#nav) this.#nav.innerHTML = nav.innerHTML;
      });

      this.#page = nextPage;
      if (this.#page >= this.#total) {
        this.#loadObserver.disconnect();
        this.#sentinel.remove();
      }

      const announcer = document.getElementById('sr-announcer');
      if (announcer) {
        announcer.textContent = '';
        cancelAnimationFrame(this.#rafId);
        this.#rafId = requestAnimationFrame(() => {
          announcer.textContent = this.dataset.loadedMessage;
          this.#rafId = null;
        });
      }
    } finally {
      this.#loading = false;
    }
  }

  async #restorePages() {
    // Separate controller so a back-button restore doesn't cancel an in-progress scroll-load (and vice versa)
    this.#restoreController?.abort();
    this.#restoreController = new AbortController();
    const signal = this.#restoreController.signal;
    const startPage = this.#page;
    const fetches = Array.from({ length: startPage - 1 }, (_, i) =>
      this.#fetchPage(i + 1, signal)
    );
    const results = await Promise.allSettled(fetches);

    const firstCard = this.#grid.querySelector('[data-page]');
    if (!firstCard) return;

    await transition(() => {
      for (let i = 0; i < results.length; i++) {
        if (results[i].status !== 'fulfilled') continue;
        const { cards } = results[i].value;
        if (!cards || cards.length === 0) continue;
        cards[0].dataset.page = i + 1;
        this.#pageObserver.observe(cards[0]);
        firstCard.before(...cards);
      }
    });

    // Scroll to the original page marker
    const target = this.#grid.querySelector(`[data-page="${startPage}"]`);
    if (target) target.scrollIntoView();
  }

  /**
   * Fetch a page's partial HTML and extract the grid items and pagination nav.
   * Uses partials.fetch() for the network request and parses the response
   * using the same <template for="..."> convention that partials.apply() uses.
   *
   * Note: partials.apply() cannot be used directly here because this component
   * lives inside the partial markers — a full apply would replace the
   * <scroll-paginate> element itself, destroying the running component.
   */
  async #fetchPage(page, signal) {
    const empty = { cards: null, nav: null };
    const url = new URL(location.href);
    url.searchParams.delete('partial');
    url.searchParams.delete('theme');
    url.searchParams.delete('session_id');
    url.searchParams.set('page', String(page));

    try {
      const html = await partials.fetch(this.#partial, { url: url.toString(), signal });

      // Parse the response using the same <template for="name"> convention
      // that partials.apply() expects
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const tpl = doc.querySelector(`template[for="${this.#partial}"]`);
      if (!tpl) return empty;

      const grid = tpl.content.querySelector(`#${this.#grid.id}`);
      if (!grid) return empty;

      const nav = tpl.content.querySelector('nav[aria-label="Pagination"]');
      const cards = Array.from(grid.children);

      return { cards, nav };
    } catch (error) {
      if (error.name !== 'AbortError') console.error('[s-scroll-paginate] fetch failed:', error);
      return empty;
    }
  }
}

customElements.define('s-scroll-paginate', ScrollPaginate);
